import { Controller } from "@hotwired/stimulus"

// Live, client-side filter for the General App Settings page. Every setting is
// already rendered on the page (one card per group, one row per setting), so we
// just show/hide rows as the admin types. Modeled on support_topic_search_controller.
//
// Visibility is owned by the Bootstrap `d-none` utility class, never inline
// styles — the markup ships the clear button and empty state with `d-none`
// already applied, and this controller only toggles that class. (`d-none` is
// `display: none !important`, so it also beats primitives that set their own
// display, e.g. `.gl-leads-search-clear { display: flex }` and
// `.gl-empty-state { display: flex }`.)
//
// Targets:
//   input       - the search box
//   clearButton - the clear-X button (shown only when the input has text)
//   card        - each settings-group card
//   row         - each setting <tr>
//   empty       - the "no results" message (hidden by default)
export default class extends Controller {
  static targets = ["input", "clearButton", "card", "row", "empty"]

  search() {
    const query = this.inputTarget.value.toLowerCase().trim()
    this.toggleClearButton(query !== "")

    if (query === "") {
      this.reset()
      return
    }

    const matchedRows = new Set(
      this.rowTargets.filter(row => this.rowHaystack(row).includes(query))
    )
    this.rowTargets.forEach(row => this.toggle(row, matchedRows.has(row)))

    let anyCardVisible = false
    this.cardTargets.forEach(card => {
      const hasVisibleRow = this.rowTargets.some(
        row => card.contains(row) && matchedRows.has(row)
      )
      this.toggle(card, hasVisibleRow)
      if (hasVisibleRow) anyCardVisible = true
    })

    this.toggleEmpty(!anyCardVisible)
  }

  clear() {
    this.inputTarget.value = ""
    this.reset()
    this.toggleClearButton(false)
    this.inputTarget.focus()
  }

  // Pressing Escape while focused in the search box clears the filter — a
  // keyboard win for an admin-heavy workflow. No-op for other keys.
  clearOnEscape(event) {
    if (event.key === "Escape") this.clear()
  }

  reset() {
    this.rowTargets.forEach(row => this.toggle(row, true))
    this.cardTargets.forEach(card => this.toggle(card, true))
    this.toggleEmpty(false)
  }

  // Build the searchable text for a row from its label + var data attributes
  // (set by setting-info-modal) plus the current-value cell. We read specific
  // sources rather than the whole row's textContent so the "Edit" button title
  // and icon markup never produce false matches. Every standard settings row
  // also carries data-app-settings-search-text set to its card title, so a
  // setting is findable by the name of the card it lives in (e.g. searching
  // "support" surfaces every row in "Admin Support Settings"); bespoke rows use
  // the same attribute to declare their own keywords.
  rowHaystack(row) {
    const label = row.dataset.settingInfoModalLabelValue || ""
    const varName = row.dataset.settingInfoModalVarValue || ""
    const searchText = row.dataset.appSettingsSearchText || ""
    const valueCell = row.querySelector(".gl-col-current-value")
    const value = valueCell ? valueCell.textContent : ""
    return `${label} ${varName} ${searchText} ${value}`.toLowerCase()
  }

  toggleEmpty(show) {
    if (this.hasEmptyTarget) {
      this.toggle(this.emptyTarget, show)
    }
  }

  toggleClearButton(show) {
    if (this.hasClearButtonTarget) {
      this.toggle(this.clearButtonTarget, show)
    }
  }

  toggle(element, visible) {
    element.classList.toggle("d-none", !visible)
  }
}
